<script lang="ts">
    import { onMount } from 'svelte';
    import { lightningDelay, lightningDelayError, Widget, widgetInfo, WidgetType } from '../../src/store';
    import { tickLightningDelay } from '../../src/refresh';
    import Checkmark from './checkmark.svelte';
    import Lightning from './lightning.svelte';
    import Dash from './dash.svelte';

    export let fill: string = 'yellow';
    export let radius: string = '46';
    let time: number = null;
    let isError: boolean = false;
    let interval = null;
    let type: WidgetType = WidgetType.DEF;
    // Declared before store subscribes: lightningDelay fires immediately with its current value.
    let allClearTimeInSeconds = 1800;
    let totalCircumference: number = 2 * Math.PI * parseInt(radius);
    let circumferenceRemoved: number = totalCircumference;

    function UpdateCircumference(seconds: number) {
        if (seconds == null || seconds <= 0 || allClearTimeInSeconds <= 0) {
            circumferenceRemoved = totalCircumference;
            return;
        }
        circumferenceRemoved = (1 - seconds / allClearTimeInSeconds) * totalCircumference;
    }

    lightningDelay.subscribe((value: number) => {
        fill = value > 0 ? '--error-color' : '--success-color';
        time = value;
        UpdateCircumference(value);
    });

    lightningDelayError.subscribe((value: boolean) => {
        isError = value;
        fill = value ? '--error-color' : '--success-color';
    });

    widgetInfo.subscribe((value: Widget) => {
        if (value != undefined && value.lightningAllClearMinutes != undefined) {
            allClearTimeInSeconds = value.lightningAllClearMinutes * 60;
            UpdateCircumference(time);
        }

        if (value?.type)
            type = value.type;
    });

    onMount(() => {
        interval = setInterval(() => {
            if (time == null || time <= 0) return;
            // Floors at 1s while active; Clear only arrives from a successful poll.
            lightningDelay.update((ld) => tickLightningDelay(ld));
        }, 1000);

        return () => {
            clearInterval(interval);
        };
    });
</script>

<div class="svg-box">
    <svg class="timer svg-content" viewBox="-50 -50 100 100">
        {#if isError}
            <circle class="clear-bar" r={radius} style="stroke: var({type === WidgetType.DEF ? "--default-color" : "--primary-color"});" fill="transparent"  />
        {:else if time > 0}
            <circle class="timer-base" r={radius} />
            <circle
                class="timer-bar"
                r={radius}
                style="--filledCircumference: {-circumferenceRemoved}; --totalCircumference: {totalCircumference}; stroke: var({fill});"
            />    
        {:else}
            <circle class="clear-bar" r={radius} style="stroke: var({fill});" fill="transparent" />
        {/if}

        {#if isError}
            <Dash x="-25" y="-25" width="50%" height="50%" fill="var({type === WidgetType.DEF ? "--default-color" : "--primary-color"})"/>
        {:else if time > 0}
            <Lightning x="-25" y="-25" width="50%" height="50%" fill="var(--error-color)" />            
        {:else}
            <Checkmark x="-25" y="-25" width="50%" height="50%" fill="var(--success-color)" />
        {/if}
    </svg>
</div>

<style>
    .timer {
        width: 100%;
        height: 100%;
    }

    .timer-bar {
        stroke-width: 0.4rem;
        fill: transparent;
        stroke-dasharray: var(--totalCircumference);
        stroke-dashoffset: var(--filledCircumference);
        rotate: -90deg;
        /* Smooth second ticks and the rare real reset; keeps the ring from snapping. */
        transition: stroke-dashoffset 0.35s linear;
    }

    .timer-base {
        stroke: rgba(145, 142, 142, 0.699);
        stroke-width: 0.4rem;
        fill: transparent;
    }
</style>
